// AI assisted development
/**
 * Local in-app notifications for related DevAgent pipeline status changes.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useDevAgentPipelines } from './useDevAgentPipelines';
import { useDocumentVisible } from './useDocumentVisibility';
import type { Pipeline } from '../types/devagent';
import type { DashboardNotification } from '../types/notification';

const STORAGE_KEY = 'monocle.pipelineStatusNotifications.v1';
const READ_KEY = 'monocle.pipelineStatusNotifications.read.v1';
const MAX_LOCAL_NOTIFICATIONS = 25;

function readStoredNotifications(): DashboardNotification[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function readReadIds(): Set<string> {
  try {
    const parsed = JSON.parse(localStorage.getItem(READ_KEY) || '[]');
    return new Set(Array.isArray(parsed) ? parsed.map(String) : []);
  } catch {
    return new Set();
  }
}

function writeStoredNotifications(items: DashboardNotification[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, MAX_LOCAL_NOTIFICATIONS)));
}

function writeReadIds(ids: Set<string>) {
  localStorage.setItem(READ_KEY, JSON.stringify(Array.from(ids).slice(0, 100)));
}

function clearStoredNotifications() {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(READ_KEY);
}

function statusLabel(pipeline: Pipeline): string {
  return pipeline.work_state || pipeline.status || 'updated';
}

function shouldNotifyPipelineChange(previous: Pipeline, current: Pipeline): boolean {
  if (previous.status !== current.status) return true;
  if (previous.work_state !== current.work_state) return true;
  if (previous.current_stage !== current.current_stage) {
    return ['failed', 'completed', 'needs_review', 'waiting_for_approval', 'blocked'].includes(
      String(current.work_state || '').toLowerCase()
    );
  }
  return false;
}

function buildNotification(previous: Pipeline, current: Pipeline): DashboardNotification {
  const now = new Date().toISOString();
  const previousLabel = statusLabel(previous);
  const currentLabel = statusLabel(current);
  return {
    id: `pipeline:${current.pipeline_id}:${current.updated_at || now}`,
    type: 'pipeline_status_changed',
    title: 'Pipeline status changed',
    body: `${current.jira_key || current.pipeline_id} changed from ${previousLabel} to ${currentLabel}.`,
    linkUrl: `/devagent/pipelines/${current.pipeline_id}`,
    source: 'DevAgent',
    entityType: 'pipeline',
    entityId: current.pipeline_id,
    metadata: {
      jiraKey: current.jira_key,
      oldStatus: previous.status,
      newStatus: current.status,
      oldWorkState: previous.work_state,
      newWorkState: current.work_state,
      currentStage: current.current_stage,
    },
    readAt: null,
    createdAt: now,
    updatedAt: now,
    isLocal: true,
  };
}

export function usePipelineStatusNotifications(enabled = true) {
  const [items, setItems] = useState<DashboardNotification[]>(() => readStoredNotifications());
  const [readIds, setReadIds] = useState<Set<string>>(() => readReadIds());
  const previousRef = useRef<Map<string, Pipeline> | null>(null);
  const isForeground = useDocumentVisible();
  const pollingEnabled = enabled && isForeground;
  const query = useDevAgentPipelines(
    { view: 'my_queue', limit: 50 },
    { enabled: pollingEnabled, refetchInterval: 5_000 }
  );

  useEffect(() => {
    if (!pollingEnabled) return;
    const pipelines = query.data?.pipelines ?? [];
    if (!pipelines.length) return;

    const previous = previousRef.current;
    const next = new Map(pipelines.map((pipeline) => [pipeline.pipeline_id, pipeline]));
    if (!previous) {
      previousRef.current = next;
      return;
    }

    const newNotifications: DashboardNotification[] = [];
    for (const pipeline of pipelines) {
      const oldPipeline = previous.get(pipeline.pipeline_id);
      if (oldPipeline && shouldNotifyPipelineChange(oldPipeline, pipeline)) {
        newNotifications.push(buildNotification(oldPipeline, pipeline));
      }
    }

    previousRef.current = next;
    if (!newNotifications.length) return;

    setItems((current) => {
      const merged = [...newNotifications, ...current];
      const deduped = Array.from(new Map(merged.map((item) => [String(item.id), item])).values())
        .slice(0, MAX_LOCAL_NOTIFICATIONS);
      writeStoredNotifications(deduped);
      return deduped;
    });
  }, [pollingEnabled, query.data?.pipelines]);

  const visibleItems = useMemo(
    () =>
      items.map((item) => ({
        ...item,
        readAt: readIds.has(String(item.id)) ? item.readAt || item.createdAt : item.readAt,
      })),
    [items, readIds]
  );

  const unreadCount = visibleItems.filter((item) => !item.readAt).length;

  const markRead = (id: string | number) => {
    setReadIds((current) => {
      const next = new Set(current);
      next.add(String(id));
      writeReadIds(next);
      return next;
    });
  };

  const markAllRead = () => {
    setReadIds((current) => {
      const next = new Set(current);
      items.forEach((item) => next.add(String(item.id)));
      writeReadIds(next);
      return next;
    });
  };

  const clearAll = () => {
    clearStoredNotifications();
    setItems([]);
    setReadIds(new Set());
  };

  return {
    items: visibleItems,
    unreadCount,
    markRead,
    markAllRead,
    clearAll,
  };
}
